import type {
  AgCartesianChartOptions,
  AgCartesianSeriesOptions
} from "ag-charts-enterprise";
import { useMemo } from "react";
import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import type { StackedChartDataPoint } from "../../../utils/stackedTransforms";
import {
  getSortedTimePeriods,
  groupTopN,
  pivotForChart
} from "../../../utils/stackedTransforms";
import { resolvePeriodLabel } from "../../../utils/timePeriodUtils";
import type { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import { STACKED_COLOURS } from "../utils/graphConstants";
import { classifyMetric } from "../utils/graphTransforms";

/** Parameters for building AG Charts options for the stacked breakdown chart. */
interface UseStackedGraphOptionsParams {
  data: StackedChartDataPoint[] | undefined;
  metricName: string;
  metaData: ForecastMetaDataType | undefined;
  /**
   * Weekly x-axis category labels: calendar (via ``gregMapping``) or the
   * fiscal period key itself (DAS-6481). Month/quarter categories are
   * unaffected.
   */
  weekLabels: ForecastTableControlValues["weekLabels"];
}

export function useStackedGraphOptions({
  data,
  metricName,
  metaData,
  weekLabels
}: UseStackedGraphOptionsParams): AgCartesianChartOptions {
  return useMemo(() => {
    if (!data || data.length === 0 || !metaData) {
      return { series: [] } as AgCartesianChartOptions;
    }

    const classification = classifyMetric(metricName, metaData);
    const isInventory = classification?.isInventory ?? false;

    const { data: grouped, dimensionOrder } = groupTopN(data);
    const timePeriods = getSortedTimePeriods(grouped);
    const chartData = pivotForChart(grouped, timePeriods);

    const series: AgCartesianSeriesOptions[] = dimensionOrder.map((dim, i) => ({
      type: isInventory ? ("bar" as const) : ("area" as const),
      xKey: "period",
      yKey: dim,
      yName: dim,
      stacked: true,
      fill: STACKED_COLOURS[i % STACKED_COLOURS.length],
      stroke: STACKED_COLOURS[i % STACKED_COLOURS.length],
      ...(isInventory ? {} : { fillOpacity: 0.6 })
    }));

    return {
      data: chartData,
      series,
      axes: {
        x: {
          type: "category",
          position: "bottom",
          label: {
            // The stacked payload is always weekly (the endpoint groups on
            // fiscal week regardless of the timePeriod control), so weekly
            // detection is by key shape, not by the control.
            formatter: ({ value }: { value: string }) =>
              resolvePeriodLabel(value, weekLabels, metaData.gregMapping)
          }
        },
        y: {
          type: "number",
          position: "left",
          min: 0
        }
      },
      legend: { position: "top" }
    } satisfies AgCartesianChartOptions;
  }, [data, metricName, metaData, weekLabels]);
}
